/** @jsxImportSource ../../i18n */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BellRing, PiggyBank, Save, Zap } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { DEPOSIT_REASONS } from '../../services/plan';
import { formatDate, formatMoney, formatPercent } from '../../theme/format';
import { Badge, Banner, Button, Card, CardHeader, Checkbox, Field, Input, Money, Select, Skeleton, SymbolMark } from '../ui';
import { BasketDialog } from '../trading/BasketDialog';

const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);
const num = (v) => {
  const n = Number(String(v).replace(',', '.').replace(/\s/g, ''));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

// Monthly deposit planner: the deposit spread over the lines below their
// saved target (computed by the server, which also sends the payday push).
// version changes when the targets are saved, to recompute the plan.
export const DepositPlanner = ({ version = 0 }) => {
  const toast = useToast();
  const [plan, setPlan] = useState(null);
  const [form, setForm] = useState(null);
  const [state, setState] = useState({ saving: false, error: '' });
  const [loadError, setLoadError] = useState('');
  const [basketOpen, setBasketOpen] = useState(false);
  const first = useRef(true);

  const fetchPlan = useCallback(async (amount) => {
    try {
      const res = await api.get('/plan/deposit', amount === undefined ? undefined : { params: { amount } });
      setPlan(res.data);
      setLoadError('');
      return res.data;
    } catch (err) {
      setLoadError(err?.response?.data?.error || 'Plan de versement indisponible.');
      return null;
    }
  }, []);

  useEffect(() => {
    fetchPlan().then((p) => {
      if (p && first.current) {
        first.current = false;
        const settings = p.settings || {};
        setForm({ amount: settings.amount ? String(settings.amount) : '', payday: settings.payday || 5, enabled: Boolean(settings.enabled) });
      }
    });
  }, [fetchPlan, version]);

  // Preview another amount without saving it.
  useEffect(() => {
    if (!form || first.current) return undefined;
    const amount = num(form.amount);
    if (amount === null || amount === plan?.settings?.amount) return undefined;
    const id = setTimeout(() => fetchPlan(amount), 500);
    return () => clearTimeout(id);
  }, [form?.amount]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (event) => {
    event.preventDefault();
    const amount = num(form.amount);
    if (amount === null) {
      setState({ saving: false, error: 'Indiquez un montant en euros.' });
      return;
    }
    setState({ saving: true, error: '' });
    try {
      const res = await api.put('/plan/deposit', { amount, payday: Number(form.payday), enabled: form.enabled });
      setPlan(res.data);
      setState({ saving: false, error: '' });
      toast.success('Versement mensuel enregistré', { description: form.enabled ? 'Un rappel arrivera le jour du versement.' : undefined });
    } catch (err) {
      setState({ saving: false, error: err?.response?.data?.error || 'Impossible d’enregistrer le versement.' });
    }
  };

  return (
    <Card as="form" onSubmit={save} data-testid="deposit-planner">
      <CardHeader
        icon={PiggyBank}
        title="Versement mensuel"
        description="Votre versement réparti vers les lignes les plus en dessous de leur cible enregistrée, en actions entières, sans toucher à la réserve de liquidités ni dépasser le plafond du PEA."
      />
      {loadError && <Banner tone="warn" className="mb-4">{loadError}</Banner>}
      {!form ? (
        <div role="status" className="space-y-3"><span className="sr-only">Chargement…</span><Skeleton className="h-11 w-full" /><Skeleton className="h-24 w-full" /></div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-2">
            <Field label="Montant versé chaque mois" htmlFor="deposit-amount">
              <Input id="deposit-amount" inputMode="decimal" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} trailing={<span className="pr-2 text-sm text-fg-subtle">€</span>} />
            </Field>
            <Field label="Jour du versement" htmlFor="deposit-day" hint="Un week-end, le rappel arrive le lundi suivant.">
              <Select id="deposit-day" value={form.payday} onChange={(e) => setForm((f) => ({ ...f, payday: Number(e.target.value) }))}>
                {DAYS.map((d) => <option key={d} value={d}>le {d} du mois</option>)}
              </Select>
            </Field>
            <label className="flex cursor-pointer items-center gap-3 text-sm text-fg-body">
              <Checkbox switch checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />
              Me rappeler les achats le jour du versement
            </label>
            {state.error && <Banner tone="loss" role="alert">{state.error}</Banner>}
            <Button type="submit" leadingIcon={Save} loading={state.saving}>Enregistrer</Button>
          </div>
          <div className="lg:col-span-3" data-testid="deposit-plan">
            {plan && (
              <>
                {plan.ceiling_limited && plan.reason !== 'ceiling_reached' && (
                  <Banner tone="warn" className="mb-4">Montant réduit à <Money value={plan.amount} decimals={0} /> : c’est ce qu’il reste avant le plafond de {formatMoney(150000, { decimals: 0 })} du PEA.</Banner>
                )}
                {plan.reason ? (
                  <p className="rounded-control border border-dashed border-line-strong px-4 py-6 text-center text-sm text-fg-muted">{DEPOSIT_REASONS[plan.reason] || 'Aucun achat proposé.'}</p>
                ) : (
                  <>
                    <p className="mb-3 text-sm text-fg-muted">
                      À acheter : <span className="font-medium text-fg">{plan.summary}</span> — budget <Money value={plan.budget} decimals={0} className="text-fg" /> (versement et liquidités au-delà de la réserve).
                    </p>
                    <ul className="divide-y divide-line">
                      {(plan.orders || []).map((o) => (
                        <li key={o.symbol} className="flex items-center gap-3 py-2.5 first:pt-0" data-testid="deposit-order">
                          <SymbolMark symbol={o.symbol} size="sm" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-fg">{o.shares} × {o.symbol}</p>
                            <p className="num text-xs text-fg-subtle">
                              {formatPercent(o.weight_before, { sign: false })} → {formatPercent(o.weight_after, { sign: false })} (cible {formatPercent(o.target_pct, { sign: false })})
                            </p>
                          </div>
                          <Money value={o.amount} decimals={0} className="text-sm text-fg" />
                        </li>
                      ))}
                    </ul>
                    <p className="mt-3 text-xs text-fg-subtle">Reste en liquidités : <Money value={plan.leftover} decimals={0} />. Derniers cours connus, frais non inclus.</p>
                    <Button size="sm" leadingIcon={Zap} className="mt-3 bg-trade-500 hover:bg-trade-400 active:bg-trade-600" onClick={() => setBasketOpen(true)}>Acheter maintenant</Button>
                    <BasketDialog
                      open={basketOpen}
                      onClose={() => setBasketOpen(false)}
                      origin="deposit"
                      reason="Versement mensuel réparti vers les lignes sous leur cible."
                      lines={(plan.orders || []).map((o) => ({ symbol: o.symbol, side: 'buy', shares: o.shares, price: o.price }))}
                    />
                  </>
                )}
                {plan.settings?.enabled && plan.next_payday && (
                  <Badge tone="gold" icon={BellRing} className="mt-4">Prochain rappel le {formatDate(plan.next_payday, 'long')}</Badge>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export default DepositPlanner;
